import { debounce } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { useState, useCallback, useMemo, useEffect } from "react";
import {
  useGetSystemDropdownsQuery,
  useLazyGetPartnersSearchListQuery,
} from "src/shared/api/system";
import {
  EFilterType,
  EFilterCalendarType,
  IFilterModel,
} from "src/shared/components/Table/model/types";
import { INameValue, EAgencies } from "src/shared/types/common.types";
import { IBaseQueryParams } from "src/shared/types/query";
import { queryCleanEmpty } from "src/shared/utils/url/query.helpers";
import { usePreviousMonth } from "../usePreviousMonth";
import { useTableSearchParams } from "../useTableSearchParams";

export interface IGetReportCommonQuery extends IBaseQueryParams {
  agency?: string;
  partner_id?: string;
  client_id?: string;
  service?: string;
  report_month: string;
  states?: string[];
  partner_name?: string;
}
interface IFiltersProps {
  partnerName: string;
  agencyFilter?: boolean;
  // TODO - in separate task after BE
  clientFilter?: boolean;
  isSummaryReport?: boolean;
}

export type ApiSortItem = {
  field: string;
  direction: "asc" | "desc";
};

function parseSortParamToGridModel(sort?: string): GridSortModel {
  if (!sort) return [];
  const desc = sort.startsWith("-");
  const field = desc ? sort.slice(1) : sort;
  if (!field) return [];

  return [{ field, sort: desc ? "desc" : "asc" }];
}

function gridModelToSortParam(model: GridSortModel): string | null {
  const first = model?.[0];
  if (!first?.field || !first.sort) return null;

  return first.sort === "desc" ? `-${first.field}` : first.field;
}

export function useGetReportsQuery({
  partnerName,
  agencyFilter,
  isSummaryReport,
}: IFiltersProps) {
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partnersList, setPartnersList] = useState<
    Array<INameValue<number> & { agency: INameValue<EAgencies> }>
  >([]);

  const {
    paramsObject,
    onPaginationModelChange,
    onSearch,
    onResetFilter,
    onFilter,
    activeTab,
    setActiveTab,
  } = useTableSearchParams<IGetReportCommonQuery>({
    tabName: "agency",
  });

  const {
    sort,
    per_page,
    page,
    partner_id,
    partner_name,
    agency,
    search,
    service,
    client_id,
    report_month,
    states,
  } = paramsObject;

  const reportMonth = usePreviousMonth();

  //sort
  const sortValue = useMemo(() => {
    if (!sort) return undefined;

    return Array.isArray(sort) ? sort[0] : sort;
  }, [sort]);

  const sortModel = useMemo(
    () => parseSortParamToGridModel(sortValue),
    [sortValue],
  );

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      const nextSort = gridModelToSortParam(model);
      onFilter([
        { name: "sort", value: nextSort },
        { name: "page", value: "1" },
      ]);
    },
    [onFilter],
  );

  const query = queryCleanEmpty({
    search,
    sort,
    per_page,
    page: String(page ? Number(page) : 0),
    partner_id,
    client_id,
    partner_name,
    agency,
    service,
    states: states && states,
    report_month: reportMonth,
  });
  const { data: dropdowns } = useGetSystemDropdownsQuery();
  const [searchPartners, { isFetching: isPartnersFetching }] =
    useLazyGetPartnersSearchListQuery();

  const searchPartner = useCallback(
    (value: string) => {
      if (value.length > 2) {
        setPartnerSearch(value);
        searchPartners({ name: value, agencies: agency ? [agency] : undefined })
          .unwrap()
          .then((resp) => {
            setPartnersList(resp);
          })
          .catch(() => {
            enqueueSnackbar("Произошла ошибка", {
              variant: "error",
              handleClose: closeSnackbar,
            });
          });
      }
    },
    [searchPartners, agency],
  );

  const debouncedPartnerSearch = useMemo(
    () => debounce(searchPartner, 1000),
    [searchPartner],
  );

  const filters: IFilterModel["filters"] = useMemo(
    () => [
      {
        name: "partner_id",
        // for example if need multiple partners
        // type: EFilterType.MULTI_AUTOCOMPLETE,
        // value: Array.isArray(partner_id)
        //   ? partner_id
        //   : partner_id
        //     ? [partner_id]
        //     : [],
        // placeholder: "Минимум 3 символа",
        loading: isPartnersFetching,
        type: EFilterType.SINGLE_AUTOCOMPLETE,
        value: partner_id || null,
        title: "Партнер",
        options: agency
          ? partnersList.filter((v) => v.agency.value === Number(agency))
          : partnersList,
        onInputChange: (_e, value, reason) => {
          if (reason !== "input") return;
          debouncedPartnerSearch(value);
        },
      },
      ...(agencyFilter
        ? [
            {
              name: "agency",
              type: EFilterType.SINGLE_SELECT,
              value: agency || "",
              title: "Компания",
              options: dropdowns?.agencies || [],
            },
          ]
        : []),
      {
        name: "report_month",
        type: EFilterCalendarType.MONTH,
        value: report_month || null,
        title: "За период",
      },
      ...(isSummaryReport
        ? []
        : [
            {
              name: "service",
              type: EFilterType.SINGLE_SELECT,
              value: service || "",
              title: "Сервис",
              options: dropdowns?.services || [],
            },
          ]),
      ...(isSummaryReport
        ? [
            {
              name: "states",
              type: EFilterType.MULTI_SELECT,
              value: Array.isArray(states) ? states : states ? [states] : [],
              title: "Статус",
              options: dropdowns?.summary_reward_states || [],
            },
          ]
        : []),
    ],
    [
      agency,
      agencyFilter,
      debouncedPartnerSearch,
      dropdowns?.agencies,
      dropdowns?.services,
      dropdowns?.summary_reward_states,
      isPartnersFetching,
      partner_id,
      partnersList,
      service,
      states,
      isSummaryReport,
      report_month,
    ],
  );

  // here we pull partners to show full name in the filter 'partner_id'
  useEffect(() => {
    if (!partnerSearch && !partnersList.length && partnerName) {
      searchPartner(partnerName);
    }
  }, [partnerName, partnerSearch, partnersList.length, searchPartner]);

  useEffect(() => {
    const id = Number(partner_id);

    if (!id) return;

    const option = partnersList.find((item) => item.value === id);

    if (!option) return;

    if (partner_name === option.name) return;

    onFilter([{ name: "partner_name", value: option.name }]);
  }, [partner_id, partner_name, partnersList, onFilter]);

  useEffect(() => {
    const id = Number(partner_id);

    if (!id) return;

    if (partnersList.some((item) => item.value == id)) return;

    if (partner_name) {
      searchPartner(partner_name);
    }
  }, [partner_id, partner_name, partnersList, searchPartner]);

  useEffect(() => {
    if (!partner_id && partner_name) {
      onFilter([
        {
          name: "partner_name",
          value: null,
        },
      ]);
    }
  }, [partner_id, partner_name, onFilter]);

  // here we filter partners by selected agency if agency changed
  useEffect(() => {
    if (!agency || !partnersList.length || !partner_id) return;

    const newPartnersList = partnersList.filter(
      (v) => v.agency.value === Number(agency),
    );

    if (!newPartnersList.some((v) => v.value === Number(partner_id))) {
      onResetFilter(["partner_id"]);
    }
  }, [agency, onResetFilter, partner_id, partnersList]);

  return {
    query,
    filters,
    onPaginationModelChange,
    onSearch,
    onResetFilter,
    onFilter,
    activeTab,
    setActiveTab,
    sortModel,
    onSortModelChange,
  };
}
